import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Link, router } from '@inertiajs/react'
import {
  ArrowLeft,
  Edit,
  Trash2,
  CheckCircle,
  Clock,
  Calendar,
  Globe,
  Users,
  FileCode,
  Eye
} from 'lucide-react'
import { useCallback, useState } from 'react'

interface PageContentDetail {
  id: number
  page_key: string
  name: string
  description: string | null
  content_type: string
  html_content: string | null
  css_content: string | null
  // gjs_data excluded from Show page to reduce payload size
  community_id: number | null
  community_name: string | null
  published: boolean
  published_at: string | null
  created_at: string
  updated_at: string
}

interface ShowProps {
  page_content: PageContentDetail
}

const contentTypeLabels: Record<string, string> = {
  banner: 'Banniere',
  footer: 'Footer',
  cta: 'CTA',
  sidebar: 'Sidebar',
  custom: 'Personnalise'
}

const contentTypeColors: Record<string, string> = {
  banner: 'bg-blue-100 text-blue-800',
  footer: 'bg-purple-100 text-purple-800',
  cta: 'bg-amber-100 text-amber-800',
  sidebar: 'bg-green-100 text-green-800',
  custom: 'bg-gray-100 text-gray-800'
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export default function Show({ page_content }: ShowProps) {
  const [showPreview, setShowPreview] = useState(false)

  const handleDelete = useCallback(() => {
    if (confirm(`Voulez-vous vraiment supprimer "${page_content.name}" ?`)) {
      router.delete(`/admin/page_contents/${page_content.id}`)
    }
  }, [page_content.id, page_content.name])

  const handlePublish = useCallback(() => {
    router.post(`/admin/page_contents/${page_content.id}/publish`, {}, {
      preserveScroll: true
    })
  }, [page_content.id])

  const handleUnpublish = useCallback(() => {
    router.post(`/admin/page_contents/${page_content.id}/unpublish`, {}, {
      preserveScroll: true
    })
  }, [page_content.id])

  const hasContent = Boolean(page_content.html_content)

  return (
    <AdminLayout
      title={page_content.name}
      breadcrumbs={[
        { label: 'Contenus editables', href: '/admin/page_contents' },
        { label: page_content.name }
      ]}
    >
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Link href="/admin/page_contents">
            <Button variant="secondary" className="flex items-center gap-2">
              <ArrowLeft size={18} />
              Retour
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-text-dark">{page_content.name}</h1>
              {page_content.published ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800">
                  <CheckCircle size={12} />
                  Publie
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-amber-100 text-amber-800">
                  <Clock size={12} />
                  Brouillon
                </span>
              )}
            </div>
            {page_content.description && (
              <p className="text-sm text-text-gray mt-1">{page_content.description}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {page_content.published ? (
            <Button variant="secondary" onClick={handleUnpublish}>
              <Clock size={18} className="mr-2" />
              Depublier
            </Button>
          ) : (
            <Button onClick={handlePublish} className="bg-green-600 hover:bg-green-700" disabled={!hasContent}>
              <CheckCircle size={18} className="mr-2" />
              Publier
            </Button>
          )}
          <Link href={`/admin/page_contents/${page_content.id}/edit`}>
            <Button className="flex items-center gap-2">
              <Edit size={18} />
              Editer
            </Button>
          </Link>
          <Button
            variant="danger"
            onClick={handleDelete}
            className="flex items-center gap-2"
          >
            <Trash2 size={18} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Preview */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-text-dark">Apercu</h2>
              {hasContent && (
                <Button variant="secondary" onClick={() => setShowPreview(true)} className="text-sm">
                  <Eye size={16} className="mr-2" />
                  Plein ecran
                </Button>
              )}
            </div>

            {hasContent ? (
              <div className="border rounded-lg p-4 bg-gray-50 min-h-[200px]">
                {page_content.css_content && (
                  <style dangerouslySetInnerHTML={{ __html: page_content.css_content }} />
                )}
                <div dangerouslySetInnerHTML={{ __html: page_content.html_content || '' }} />
              </div>
            ) : (
              <div className="border rounded-lg p-8 bg-gray-50 text-center">
                <FileCode size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-text-gray mb-4">Aucun contenu pour le moment</p>
                <Link href={`/admin/page_contents/${page_content.id}/edit`}>
                  <Button>
                    <Edit size={18} className="mr-2" />
                    Creer le contenu
                  </Button>
                </Link>
              </div>
            )}
          </Card>

          {/* Code */}
          {hasContent && (
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-text-dark mb-4">Code genere</h2>

              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-text-dark mb-2">HTML</h3>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto max-h-48">
                    {page_content.html_content}
                  </pre>
                </div>

                {page_content.css_content && (
                  <div>
                    <h3 className="text-sm font-medium text-text-dark mb-2">CSS</h3>
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto max-h-48">
                      {page_content.css_content}
                    </pre>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Details */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-text-dark mb-4">Details</h2>

            <div className="space-y-4">
              <div>
                <p className="text-xs text-text-gray uppercase tracking-wide mb-1">Cle</p>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded text-text-dark">
                  {page_content.page_key}
                </code>
              </div>

              <div>
                <p className="text-xs text-text-gray uppercase tracking-wide mb-1">Type</p>
                <span className={`inline-flex px-2 py-1 text-sm font-medium rounded ${contentTypeColors[page_content.content_type] || contentTypeColors.custom}`}>
                  {contentTypeLabels[page_content.content_type] || page_content.content_type}
                </span>
              </div>

              <div>
                <p className="text-xs text-text-gray uppercase tracking-wide mb-1">Communaute</p>
                <div className="flex items-center gap-2">
                  {page_content.community_name ? (
                    <>
                      <Users size={16} className="text-text-gray" />
                      <span className="text-sm text-text-dark">{page_content.community_name}</span>
                    </>
                  ) : (
                    <>
                      <Globe size={16} className="text-text-gray" />
                      <span className="text-sm text-text-dark">Global</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* Dates */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-text-dark mb-4">Historique</h2>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Calendar size={16} className="text-text-gray mt-0.5" />
                <div>
                  <p className="text-xs text-text-gray uppercase tracking-wide">Cree le</p>
                  <p className="text-sm text-text-dark">{formatDate(page_content.created_at)}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock size={16} className="text-text-gray mt-0.5" />
                <div>
                  <p className="text-xs text-text-gray uppercase tracking-wide">Modifie le</p>
                  <p className="text-sm text-text-dark">{formatDate(page_content.updated_at)}</p>
                </div>
              </div>

              {page_content.published_at && (
                <div className="flex items-start gap-3">
                  <CheckCircle size={16} className="text-green-600 mt-0.5" />
                  <div>
                    <p className="text-xs text-text-gray uppercase tracking-wide">Publie le</p>
                    <p className="text-sm text-text-dark">{formatDate(page_content.published_at)}</p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Usage */}
          <Card className="p-6 bg-tessan-cream/30">
            <h2 className="text-lg font-semibold text-text-dark mb-4">Integration</h2>
            <p className="text-sm text-text-gray mb-3">
              Utilisez cette cle pour afficher ce contenu dans vos templates :
            </p>
            <pre className="bg-gray-900 text-gray-100 p-3 rounded-lg text-xs overflow-x-auto">
              {`<%= render_page_content('${page_content.page_key}') %>`}
            </pre>
          </Card>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-text-dark">Apercu - {page_content.name}</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-text-gray hover:text-text-dark"
              >
                Fermer
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-60px)]">
              {page_content.css_content && (
                <style dangerouslySetInnerHTML={{ __html: page_content.css_content }} />
              )}
              <div dangerouslySetInnerHTML={{ __html: page_content.html_content || '' }} />
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}
